import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = axios.create({ baseURL: API_URL });

/**
 * Channel rooms — one shared chat per public channel (Top 100, Rec, OTK…).
 * Same wire shape as a watch party's chat, minus the host: anyone can listen
 * over SSE, joining with a nickname is only needed to talk.
 */

export interface ChannelParticipant {
  id: string;
  nickname: string;
}

export interface ChannelMessage {
  id: string;
  participantId: string;
  nickname: string;
  text: string;
  timestamp: number;
}

export interface ChannelPresence {
  /** Everyone tuned in, lurkers included */
  online: number;
  /** People who joined with a nickname (can post) */
  participants: ChannelParticipant[];
}

export interface ChannelRoom extends ChannelPresence {
  channel: string;
  messages: ChannelMessage[];
  createdAt: number;
}

export interface JoinChannelResult {
  participantId: string;
  nickname: string;
  room: ChannelRoom;
}

const roomPath = (room: string): string => `/api/channel/${encodeURIComponent(room)}`;

export async function joinChannelRoom(room: string, nickname: string): Promise<JoinChannelResult> {
  const res = await api.post(`${roomPath(room)}/join`, { nickname });
  return res.data;
}

export async function sendChannelMessage(room: string, participantId: string, text: string): Promise<ChannelMessage> {
  const res = await api.post(`${roomPath(room)}/message`, { participantId, text });
  return res.data.message;
}

export async function channelHeartbeat(room: string, participantId: string): Promise<void> {
  await api.post(`${roomPath(room)}/heartbeat`, { participantId });
}

export async function leaveChannelRoom(room: string, participantId: string): Promise<void> {
  try {
    await api.post(`${roomPath(room)}/leave`, { participantId });
  } catch {
    // best-effort — the server prunes silent participants anyway
  }
}

export type ChannelEventHandlers = {
  onState?: (room: ChannelRoom) => void;
  onPresence?: (presence: ChannelPresence) => void;
  onMessage?: (message: ChannelMessage) => void;
  onError?: () => void;
};

/** Listen to a room. EventSource reconnects on its own; every (re)connect
 *  starts with a full `state` event so the client can resync. */
export function subscribeChannelRoom(room: string, handlers: ChannelEventHandlers): () => void {
  const es = new EventSource(`${API_URL}${roomPath(room)}/events`);

  es.addEventListener('state', (ev) => {
    try { handlers.onState?.(JSON.parse((ev as MessageEvent).data)); } catch {}
  });
  es.addEventListener('presence', (ev) => {
    try { handlers.onPresence?.(JSON.parse((ev as MessageEvent).data)); } catch {}
  });
  es.addEventListener('message', (ev) => {
    try { handlers.onMessage?.(JSON.parse((ev as MessageEvent).data)); } catch {}
  });
  es.onerror = () => { handlers.onError?.(); };

  return () => es.close();
}
